import { baseApi } from '@shared/api/baseApi';
import type { ProductDto } from '@entities/product';

export interface SmsPoolServiceDto {
  id: number;
  name: string;
}

export interface SmsPoolCountryDto {
  id: number;
  name: string;
  shortName: string;
  region: string;
}

/** Live price and stock for one (service, country, pool) cell. */
export interface SmsPoolQuoteDto {
  serviceId: number;
  countryId: number;
  pool: number;
  priceUSD: string;
  stock: number;
}

export interface PinnedNumberDto extends ProductDto {
  smspool: {
    serviceId: number;
    countryId: number;
    pool: number;
    serviceName: string | null;
    countryName: string | null;
  } | null;
  /** Our upstream cost, and the ceiling every purchase is capped at. */
  costUSD: string;
  finiteStock: boolean;
}

export interface PinNumberRequest {
  serviceId: number;
  countryId: number;
  pool?: number;
  name?: string | null;
  markupETB?: string | null;
  priceETB?: string | null;
  categoryId?: string | null;
  details?: string | null;
}

/** The global rule every pinned number is priced from. */
export interface SmsPricingSettingsDto {
  /** ETB per US dollar. SMSPool quotes in USD, not USDT. */
  usdEtbRate: string;
  /** Applied on top of the converted cost, e.g. "2" sells at double. */
  multiplier: string;
}

/** One service's override, applying to that service in every country. */
export interface SmsServiceRuleDto {
  serviceId: number;
  /** Whether customers may browse and buy this service at all. */
  enabled: boolean;
  serviceName: string | null;
  multiplier: string | null;
  fixedPriceETB: string | null;
}

/** Who can see a section or the numbers feature. */
export type Visibility = 'PUBLIC' | 'ADMIN_ONLY' | 'HIDDEN';

/** A shop feature card, as the panel edits it. */
export interface AdminShopServiceDto {
  slug: string;
  name: string;
  description: string | null;
  categoryId: string | null;
  categoryName: string | null;
  visibility: Visibility;
  logoUrl: string;
}

export interface AdminCategoryDto {
  id: string;
  slug: string;
  name: string;
  sortOrder: number;
  visibility: Visibility;
}

/**
 * The operator's side of SMSPool and the storefront's sections.
 *
 * Services and countries are ~1,500 and ~200 rows and change rarely, so they
 * are held with `keepUnusedDataFor` well past the default: the picker would
 * otherwise re-fetch both lists every time the panel is reopened. The server
 * caches them for an hour too, so this only avoids the round trip.
 */
export const smspoolApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getSmsPoolServices: builder.query<SmsPoolServiceDto[], void>({
      query: () => '/admin/smspool/services',
      transformResponse: (response: { services: SmsPoolServiceDto[] }) => response.services,
      keepUnusedDataFor: 3600,
      // Re-asked whenever the tab is opened. Without this the hour-long cache
      // also holds on to a *failure*: someone who looks before the API key is
      // configured gets the "not connected" screen for an hour after it is,
      // with no way out but restarting the app.
    }),
    getSmsPoolCountries: builder.query<SmsPoolCountryDto[], void>({
      query: () => '/admin/smspool/countries',
      transformResponse: (response: { countries: SmsPoolCountryDto[] }) => response.countries,
      keepUnusedDataFor: 3600,
    }),
    /** Quotes one cell before it is pinned, so the price is never a guess. */
    getSmsPoolQuote: builder.query<
      SmsPoolQuoteDto | null,
      { service: number; country: number; pool?: number }
    >({
      query: ({ service, country, pool = 0 }) => ({
        url: '/admin/smspool/quote',
        params: { service, country, pool },
      }),
      transformResponse: (response: { quote: SmsPoolQuoteDto | null }) => response.quote,
    }),
    getPinnedNumbers: builder.query<PinnedNumberDto[], void>({
      query: () => '/admin/smspool/pinned',
      transformResponse: (response: { products: PinnedNumberDto[] }) => response.products,
      providesTags: ['PinnedNumber'],
    }),
    pinNumber: builder.mutation<PinnedNumberDto, PinNumberRequest>({
      query: (body) => ({ url: '/admin/smspool/pinned', method: 'POST', body }),
      transformResponse: (response: { product: PinnedNumberDto }) => response.product,
      // It goes on sale immediately, so the storefront catalogue is stale too.
      invalidatesTags: ['PinnedNumber', 'Product', 'AdminProduct'],
    }),
    unpinNumber: builder.mutation<{ outcome: string }, string>({
      query: (slug) => ({ url: `/admin/smspool/pinned/${slug}`, method: 'DELETE' }),
      invalidatesTags: ['PinnedNumber', 'Product', 'AdminProduct'],
    }),
    refreshSmsPoolQuotes: builder.mutation<{ checked: number; updated: number }, void>({
      query: () => ({ url: '/admin/smspool/refresh', method: 'POST' }),
      invalidatesTags: ['PinnedNumber', 'Product', 'AdminProduct'],
    }),
    getSmsPoolBalance: builder.query<string, void>({
      query: () => '/admin/smspool/balance',
      transformResponse: (response: { balanceUSD: string }) => response.balanceUSD,
    }),

    getSmsPricing: builder.query<
      { settings: SmsPricingSettingsDto; rules: SmsServiceRuleDto[] },
      void
    >({
      query: () => '/admin/smspool/pricing',
      providesTags: ['SmsPricing'],
    }),
    saveSmsPricing: builder.mutation<
      { settings: SmsPricingSettingsDto; repriced: number },
      { usdEtbRate?: string; multiplier?: string }
    >({
      query: (body) => ({ url: '/admin/smspool/pricing', method: 'PUT', body }),
      // Repricing runs server-side in the same call, so every pinned number
      // and the storefront catalogue are both stale the moment this returns.
      invalidatesTags: ['SmsPricing', 'PinnedNumber', 'Product', 'AdminProduct'],
    }),
    saveSmsServiceRule: builder.mutation<
      { rule: SmsServiceRuleDto; repriced: number },
      { serviceId: number; multiplier?: string; fixedPriceETB?: string }
    >({
      query: ({ serviceId, ...body }) => ({
        url: `/admin/smspool/pricing/rules/${serviceId}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['SmsPricing', 'PinnedNumber', 'Product', 'AdminProduct'],
    }),
    deleteSmsServiceRule: builder.mutation<{ repriced: number }, number>({
      query: (serviceId) => ({
        url: `/admin/smspool/pricing/rules/${serviceId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['SmsPricing', 'PinnedNumber', 'Product', 'AdminProduct'],
    }),

    /**
     * The master switch for rented numbers, across every section.
     *
     * Its own endpoint outside the SMSPool group so it stays usable with no
     * API key configured — turning the feature off must not depend on the
     * thing being turned off.
     */
    getNumbersVisibility: builder.query<Visibility, void>({
      query: () => '/admin/numbers-visibility',
      transformResponse: (response: { visibility: Visibility }) => response.visibility,
      providesTags: ['NumbersVisibility'],
    }),
    setNumbersVisibility: builder.mutation<Visibility, Visibility>({
      query: (visibility) => ({
        url: '/admin/numbers-visibility',
        method: 'PUT',
        body: { visibility },
      }),
      transformResponse: (response: { visibility: Visibility }) => response.visibility,
      // Changes what customers can buy, so the storefront catalogue is stale.
      invalidatesTags: ['NumbersVisibility', 'Product', 'Category'],
    }),

    /**
     * Turns a service on or off for customers.
     *
     * One call covers every country SMSPool has it in, which is what makes
     * choosing what you sell a per-service decision instead of a per-country
     * one repeated 150 times.
     */
    setServiceEnabled: builder.mutation<
      { rule: SmsServiceRuleDto },
      { serviceId: number; enabled: boolean }
    >({
      query: ({ serviceId, enabled }) => ({
        url: `/admin/smspool/services/${serviceId}/enabled`,
        method: 'PUT',
        body: { enabled },
      }),
      invalidatesTags: ['SmsPricing', 'NumberService', 'Product'],
    }),

    // --- shop service cards ---
    getAdminShopServices: builder.query<AdminShopServiceDto[], void>({
      query: () => '/admin/services',
      transformResponse: (response: { services: AdminShopServiceDto[] }) => response.services,
      providesTags: ['AdminShopService'],
    }),
    updateShopService: builder.mutation<
      { service: AdminShopServiceDto },
      {
        slug: string;
        name?: string;
        description?: string | null;
        categoryId?: string | null;
        visibility?: Visibility;
      }
    >({
      query: ({ slug, ...body }) => ({ url: `/admin/services/${slug}`, method: 'PUT', body }),
      invalidatesTags: ['AdminShopService', 'ShopService'],
    }),
    setShopServiceLogo: builder.mutation<
      { service: AdminShopServiceDto },
      { slug: string; imageBase64: string; background?: 'auto' | 'transparent' | 'white' }
    >({
      query: ({ slug, ...body }) => ({
        url: `/admin/services/${slug}/logo`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['AdminShopService', 'ShopService'],
    }),

    // --- storefront sections ---
    getAdminCategories: builder.query<AdminCategoryDto[], void>({
      query: () => '/admin/categories',
      transformResponse: (response: { categories: AdminCategoryDto[] }) => response.categories,
      providesTags: ['AdminCategory'],
    }),
    createCategory: builder.mutation<AdminCategoryDto, { name: string }>({
      query: (body) => ({ url: '/admin/categories', method: 'POST', body }),
      transformResponse: (response: { category: AdminCategoryDto }) => response.category,
      invalidatesTags: ['AdminCategory', 'Category', 'Product'],
    }),
    updateCategory: builder.mutation<
      AdminCategoryDto,
      { id: string; name?: string; visibility?: Visibility }
    >({
      query: ({ id, ...body }) => ({ url: `/admin/categories/${id}`, method: 'PATCH', body }),
      transformResponse: (response: { category: AdminCategoryDto }) => response.category,
      invalidatesTags: ['AdminCategory', 'Category', 'Product'],
    }),
    reorderCategories: builder.mutation<{ ok: boolean }, string[]>({
      query: (ids) => ({ url: '/admin/categories/order', method: 'POST', body: { ids } }),
      invalidatesTags: ['AdminCategory', 'Category'],
    }),
    deleteCategory: builder.mutation<{ ok: boolean }, string>({
      query: (id) => ({ url: `/admin/categories/${id}`, method: 'DELETE' }),
      // Products in it survive as uncategorised, so the catalogue changes too.
      invalidatesTags: ['AdminCategory', 'Category', 'Product', 'AdminProduct'],
    }),
    setProductCategory: builder.mutation<
      { product: ProductDto },
      { slug: string; categoryId: string | null }
    >({
      query: ({ slug, categoryId }) => ({
        url: `/admin/products/${slug}/category`,
        method: 'POST',
        body: { categoryId },
      }),
      invalidatesTags: ['Product', 'AdminProduct', 'PinnedNumber', 'Category'],
    }),
  }),
});

export const {
  useGetSmsPoolServicesQuery,
  useGetSmsPoolCountriesQuery,
  useGetSmsPoolQuoteQuery,
  useGetPinnedNumbersQuery,
  usePinNumberMutation,
  useUnpinNumberMutation,
  useRefreshSmsPoolQuotesMutation,
  useGetSmsPoolBalanceQuery,
  useGetSmsPricingQuery,
  useSetServiceEnabledMutation,
  useSaveSmsPricingMutation,
  useSaveSmsServiceRuleMutation,
  useDeleteSmsServiceRuleMutation,
  useGetNumbersVisibilityQuery,
  useSetNumbersVisibilityMutation,
  useGetAdminShopServicesQuery,
  useUpdateShopServiceMutation,
  useSetShopServiceLogoMutation,
  useGetAdminCategoriesQuery,
  useCreateCategoryMutation,
  useUpdateCategoryMutation,
  useReorderCategoriesMutation,
  useDeleteCategoryMutation,
  useSetProductCategoryMutation,
} = smspoolApi;
